import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { billingQueueApi } from '../lib/api';
import { InvoiceModal, PaymentModal, MovementsModal } from '../components/project/economic/BillingWorkflowModals';
import {
  FileText, Hammer, Package, Sofa, Search, RefreshCw,
  Inbox, CheckCircle2, Euro, ChevronRight, AlertTriangle, Clock, Receipt, Wallet, ExternalLink,
} from 'lucide-react';

const SOURCE_META = {
  CONTRACT: { label: 'Contratto', Icon: FileText, color: '#FF7819' },
  EXTRA_WORK: { label: 'Extra lavori', Icon: Hammer, color: '#0078C8' },
  EXTRA_SUPPLY: { label: 'Extra forniture', Icon: Package, color: '#7C3AED' },
  FURNITURE: { label: 'Arredi', Icon: Sofa, color: '#059669' },
};

// source_type (lowercase) del workflow economico unificato
const ECO_SOURCE_META = {
  milestone: { label: 'Contratto', Icon: FileText, color: '#FF7819' },
  extra_work: { label: 'Extra lavori', Icon: Hammer, color: '#0078C8' },
  extra_supply: { label: 'Extra forniture', Icon: Package, color: '#7C3AED' },
  furniture: { label: 'Arredi', Icon: Sofa, color: '#059669' },
};

const fmtCurrency = (v) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', useGrouping: 'always' }).format(v || 0);
const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return '—'; }
};

const StatusBadge = ({ status }) => {
  const open = status === 'open';
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
        open ? 'bg-[#FFF1E6] text-[#C2410C]' : 'bg-[#ECFDF5] text-[#047857]'
      }`}
    >
      {open ? 'Da gestire' : 'Risolta'}
    </span>
  );
};

const KpiCard = ({ Icon, label, value, accent, testid }) => (
  <div
    data-testid={testid}
    className="rounded-xl border border-[#E4E4E4] bg-white shadow-sm p-5 flex items-center gap-4"
    style={{ borderLeft: `4px solid ${accent}` }}
  >
    <div className="rounded-lg p-3" style={{ background: `${accent}1A` }}>
      <Icon className="w-6 h-6" style={{ color: accent }} />
    </div>
    <div>
      <p className="text-sm font-medium text-[#525252]">{label}</p>
      <p className="text-2xl font-semibold text-[#171717] mt-0.5 leading-tight">{value}</p>
    </div>
  </div>
);

const ECO_TABS = [
  { k: 'to_invoice', l: 'Da fatturare', kpi: 'to_invoice' },
  { k: 'to_collect', l: 'Da incassare', kpi: 'to_collect' },
  { k: 'overdue', l: 'Scadute', kpi: 'overdue' },
  { k: 'collected', l: 'Incassate', kpi: 'collected' },
];

// Etichetta tranche dal modello reale (NON inferita dal testo): milestone contrattuale
// vs acconto/saldo (Extra forniture, Arredi). Fonte: campo tranche_type del read-model.
const TRANCHE_LABEL = { milestone: 'Milestone', acconto: 'Acconto', saldo: 'Saldo' };
const trancheLabel = (r) => TRANCHE_LABEL[r?.tranche_type] || '—';

const LegacyBadge = () => (
  <span className="inline-flex flex-col gap-0.5" data-testid="legacy-badge">
    <span className="inline-flex items-center w-fit text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded">Legacy</span>
    <span className="text-xs text-[#A3A3A3]">Dati fattura storici non disponibili</span>
  </span>
);

const TAB_TO_ECO = { da_fatturare: 'to_invoice', da_incassare: 'to_collect', scadute: 'overdue', incassate: 'collected' };

const EconomicSection = ({ eco, ecoLoading, ecoTab, setEcoTab, navigate, reload, deepLink, clearDeepLink }) => {
  const kpi = eco?.kpi || { to_invoice: 0, to_collect: 0, overdue: 0, collected: 0 };
  const rows = eco?.[ecoTab] || [];
  const canAct = !!eco; // il backend /billing/global è già ristretto ai ruoli BILLING 1.4
  const [wf, setWf] = useState({ type: null, tranche: null });
  const [hiKey, setHiKey] = useState(null); // highlight persistente della riga (deep-link)
  const closeWf = () => { setWf({ type: null, tranche: null }); setHiKey(null); };
  const rowKey = (r) => `${r.source_type}:${r.source_id}:${r.tranche_key}`;

  const primaryActionFor = () => (ecoTab === 'to_invoice' ? 'invoice' : ecoTab === 'collected' ? 'movements' : 'payment');
  const openAction = (type, r) => setWf({ type, tranche: r });
  const goProject = (e, r) => { e.stopPropagation(); navigate(`/projects/${r.project_id}?area=economico&sub=payments`); };
  const selectTab = (k) => { setHiKey(null); setEcoTab(k); };

  // Deep-link: evidenzia la riga (persistente) e, se action-oriented, apre la modale corretta.
  useEffect(() => {
    if (!deepLink || !eco) return;
    const key = `${deepLink.source_type}:${deepLink.source_id}:${deepLink.tranche_key}`;
    setHiKey(key);
    if (canAct && deepLink.action) {
      const match = (eco[ecoTab] || []).find((r) => rowKey(r) === key);
      if (match) setWf({ type: deepLink.action, tranche: match });
    }
    clearDeepLink && clearDeepLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink, eco]);

  return (
    <div data-testid="billing-economic-view">
      {/* KPI monetari */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard testid="eco-kpi-to-invoice" Icon={Receipt} label="Importo da fatturare" value={fmtCurrency(kpi.to_invoice)} accent="#FF7819" />
        <KpiCard testid="eco-kpi-to-collect" Icon={Wallet} label="Importo da incassare" value={fmtCurrency(kpi.to_collect)} accent="#0078C8" />
        <KpiCard testid="eco-kpi-overdue" Icon={AlertTriangle} label="Importo scaduto" value={fmtCurrency(kpi.overdue)} accent="#DC2626" />
        <KpiCard testid="eco-kpi-collected" Icon={CheckCircle2} label={`Incassato (${eco?.collected_period || 'all-time'})`} value={fmtCurrency(kpi.collected)} accent="#059669" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#F5F5F5] rounded-full p-1 mb-4 w-fit" data-testid="eco-tabs">
        {ECO_TABS.map((t) => {
          const count = (eco?.[t.k] || []).length;
          return (
            <button key={t.k} data-testid={`eco-tab-${t.k}`} onClick={() => selectTab(t.k)}
              className={`px-4 py-1.5 text-sm rounded-full transition-colors ${ecoTab === t.k ? 'bg-[#FF7819] text-white' : 'text-[#525252] hover:bg-[#EDEDED]'}`}>
              {t.l} <span className={`ml-1 ${ecoTab === t.k ? 'text-white/80' : 'text-[#A3A3A3]'}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Tabella */}
      <div className="rounded-xl border border-[#E4E4E4] bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="eco-table">
            <thead>
              <tr className="bg-[#FAFAFA] text-left text-xs font-semibold text-[#737373] uppercase tracking-wide">
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3">Progetto</th>
                <th className="px-4 py-3">Origine</th>
                <th className="px-4 py-3">Riferimento</th>
                {ecoTab === 'to_invoice' && <th className="px-4 py-3">Tranche</th>}
                {ecoTab !== 'to_invoice' && <th className="px-4 py-3">Fattura</th>}
                {(ecoTab === 'to_collect' || ecoTab === 'overdue') && <th className="px-4 py-3">Scadenza</th>}
                <th className="px-4 py-3 text-right">{ecoTab === 'to_invoice' ? 'Da fatturare' : ecoTab === 'collected' ? 'Incassato' : 'Residuo'}</th>
                <th className="px-4 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {ecoLoading ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-[#A3A3A3]">Caricamento…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-[#A3A3A3]" data-testid="eco-empty">Nessuna voce in questa vista</td></tr>
              ) : (
                rows.map((r, i) => {
                  const meta = ECO_SOURCE_META[r.source_type] || { label: r.source_label || '—', Icon: FileText, color: '#737373' };
                  const Icon = meta.Icon;
                  const amount = ecoTab === 'to_invoice' ? r.amount : ecoTab === 'collected' ? r.paid : r.outstanding;
                  const isHi = hiKey && rowKey(r) === hiKey;
                  return (
                    <tr key={`${r.project_id}-${r.source_id}-${r.tranche_key}-${i}`}
                      data-testid={`eco-row-${r.source_type}-${r.source_id}-${r.tranche_key}`}
                      onClick={() => canAct && openAction(primaryActionFor(), r)}
                      className={`border-t border-[#F0F0F0] transition-colors ${canAct ? 'hover:bg-[#FFF9F4] cursor-pointer' : ''} ${isHi ? 'bg-[#FFF4E8] ring-1 ring-inset ring-[#FF7819]' : ''}`}>
                      <td className="px-4 py-3 font-medium text-[#171717]">{r.client_name || '—'}</td>
                      <td className="px-4 py-3 text-[#525252]">{r.project_number || '—'}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 font-medium" style={{ color: meta.color }}>
                          <Icon className="w-4 h-4" /> {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[#525252]">{r.source_ref || r.code || '—'}</td>
                      {ecoTab === 'to_invoice' && (
                        <td className="px-4 py-3" data-testid={`eco-tranche-${r.source_type}-${r.source_id}-${r.tranche_key}`}>
                          <span className="inline-flex items-center rounded-full bg-[#F1F5F9] text-[#475569] px-2 py-0.5 text-xs font-medium">{trancheLabel(r)}</span>
                        </td>
                      )}
                      {ecoTab !== 'to_invoice' && (
                        <td className="px-4 py-3 text-[#525252]">
                          {r.is_legacy ? <LegacyBadge /> : (r.invoice_number || '—')}
                        </td>
                      )}
                      {(ecoTab === 'to_collect' || ecoTab === 'overdue') && (
                        <td className="px-4 py-3 whitespace-nowrap">
                          {r.due_unavailable
                            ? <span className="text-xs text-[#A3A3A3] italic" data-testid="due-unavailable">Scadenza non disponibile</span>
                            : r.is_overdue
                              ? <span className="inline-flex items-center gap-1 text-red-600 font-medium"><Clock className="w-3.5 h-3.5" /> {fmtDate(r.due_date)} ({r.days_overdue}gg)</span>
                              : <span className="text-[#525252]">{fmtDate(r.due_date)}</span>}
                        </td>
                      )}
                      <td className="px-4 py-3 text-right font-semibold text-[#171717] whitespace-nowrap">{fmtCurrency(amount)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-2 justify-end">
                          {canAct && ecoTab === 'to_invoice' && (
                            <button data-testid={`eco-action-invoice-${r.source_type}-${r.source_id}-${r.tranche_key}`}
                              onClick={() => openAction('invoice', r)}
                              className="inline-flex items-center gap-1 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 text-xs font-medium transition-colors">
                              <Receipt className="w-3.5 h-3.5" /> Registra fattura
                            </button>
                          )}
                          {canAct && (ecoTab === 'to_collect' || ecoTab === 'overdue') && (
                            <button data-testid={`eco-action-payment-${r.source_type}-${r.source_id}-${r.tranche_key}`}
                              onClick={() => openAction('payment', r)}
                              className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 text-xs font-medium transition-colors">
                              <Wallet className="w-3.5 h-3.5" /> Registra incasso
                            </button>
                          )}
                          {ecoTab === 'collected' && (
                            <button data-testid={`eco-action-movements-${r.source_type}-${r.source_id}-${r.tranche_key}`}
                              onClick={() => openAction('movements', r)}
                              className="inline-flex items-center gap-1 rounded-lg border border-[#E4E4E4] hover:bg-[#F5F5F5] text-[#525252] px-3 py-1.5 text-xs font-medium transition-colors">
                              Apri <ChevronRight className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button data-testid={`eco-goproject-${r.source_type}-${r.source_id}-${r.tranche_key}`}
                            onClick={(e) => goProject(e, r)} title="Vai al progetto"
                            className="inline-flex items-center gap-1 text-[#A3A3A3] hover:text-[#FF7819] text-xs transition-colors">
                            <ExternalLink className="w-3.5 h-3.5" /> Progetto
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modali workflow BILLING 1.4 — operative direttamente dalla Control Room */}
      <InvoiceModal open={wf.type === 'invoice'} onClose={closeWf} projectId={wf.tranche?.project_id}
        tranche={wf.type === 'invoice' ? wf.tranche : null} onDone={reload} />
      <PaymentModal open={wf.type === 'payment'} onClose={closeWf} projectId={wf.tranche?.project_id}
        tranche={wf.type === 'payment' ? wf.tranche : null} onDone={reload} />
      <MovementsModal open={wf.type === 'movements'} onClose={closeWf} projectId={wf.tranche?.project_id}
        tranche={wf.type === 'movements' ? wf.tranche : null} onDone={reload} />
    </div>
  );
};

const BillingQueuePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ to_handle: 0, resolved: 0, total_to_invoice: 0 });
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);
  const [statusFilter, setStatusFilter] = useState('open');
  const [sourceFilter, setSourceFilter] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  // FASE BILLING 1.4 — vista Workflow economico (4 tab + KPI monetari)
  const [view, setView] = useState('economico'); // 'economico' | 'richieste'
  const [ecoTab, setEcoTab] = useState('to_invoice');
  const [eco, setEco] = useState(null);
  const [ecoLoading, setEcoLoading] = useState(true);
  const [deepLink, setDeepLink] = useState(null); // {source_type, source_id, tranche_key, action}

  const loadEco = useCallback(async () => {
    setEcoLoading(true);
    try {
      const res = await fetch('/api/billing/global', { credentials: 'include' });
      setEco(await res.json());
    } catch (e) { setEco(null); } finally { setEcoLoading(false); }
  }, []);
  useEffect(() => { loadEco(); }, [loadEco]);

  // Deep-link da pagina progetto -> Control Room (identità deterministica, no parsing testo)
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const st = q.get('source_type');
    if (!st) return;
    setView('economico');
    const tab = q.get('tab');
    if (tab && TAB_TO_ECO[tab]) setEcoTab(TAB_TO_ECO[tab]);
    setDeepLink({
      source_type: st,
      source_id: q.get('source_id'),
      tranche_key: q.get('tranche_key'),
      action: q.get('action') || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const clearDeepLink = useCallback(() => {
    setDeepLink(null);
    navigate('/billing-queue', { replace: true });
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { status: statusFilter };
      if (sourceFilter) params.source_type = sourceFilter;
      const [listRes, statsRes] = await Promise.all([
        billingQueueApi.getAll(params),
        billingQueueApi.getStats(),
      ]);
      setRows(listRes.data || []);
      setStats(statsRes.data || { to_handle: 0, resolved: 0, total_to_invoice: 0 });
    } catch (e) {
      setRows([]);
      setError('Impossibile caricare la coda di fatturazione. Riprova.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sourceFilter]);

  useEffect(() => { load(); }, [load]);

  // Refresh unificato post-azione: aggiorna tab economici + KPI + contatori richieste (badge).
  const reloadEco = useCallback(async () => {
    await Promise.all([loadEco(), load()]);
  }, [loadEco, load]);

  const handleReconcile = async () => {
    if (!window.confirm('Riconciliare la coda di fatturazione? Verranno create/risolte le richieste mancanti o non più valide (nessuno stato economico viene modificato).')) {
      return;
    }
    setReconciling(true);
    setError('');
    try {
      await billingQueueApi.reconcile(false);
      await load();
    } catch (e) {
      setError('Riconciliazione non riuscita. Riprova.');
    } finally {
      setReconciling(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.client_name, r.project_number, r.source_ref, r.description, r.tranche_type]
        .filter(Boolean)
        .some((f) => String(f).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const openRow = (r) => {
    navigate(`/projects/${r.project_id}?area=economico&sub=payments`);
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="billing-queue-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <p className="text-xs font-semibold tracking-wide text-[#FF7819] uppercase">Contabilità</p>
          <h1 className="text-3xl font-semibold text-[#171717] mt-1">Control Room Fatturazione</h1>
          <p className="text-sm text-[#737373] mt-1">
            Coda unificata di tutti i cantieri — Contratto, Extra lavori, Extra forniture e Arredi.
          </p>
          <div className="flex gap-1 bg-[#F5F5F5] rounded-full p-1 mt-3 w-fit" data-testid="billing-view-toggle">
            {[{ k: 'economico', l: 'Workflow economico' }, { k: 'richieste', l: 'Richieste' }].map((o) => (
              <button key={o.k} data-testid={`billing-view-${o.k}`} onClick={() => setView(o.k)}
                className={`px-4 py-1.5 text-sm rounded-full transition-colors ${view === o.k ? 'bg-white shadow-sm text-[#171717] font-medium' : 'text-[#737373] hover:text-[#171717]'}`}>
                {o.l}
              </button>
            ))}
          </div>
        </div>
        <button
          data-testid="reconcile-btn"
          onClick={handleReconcile}
          disabled={reconciling}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#E4E4E4] bg-white text-sm font-medium text-[#525252] hover:bg-[#F5F5F5] transition-colors disabled:opacity-60"
        >
          <RefreshCw className={`w-4 h-4 ${reconciling ? 'animate-spin' : ''}`} />
          Riconcilia coda
        </button>
      </div>

      {error && (
        <div data-testid="billing-queue-error" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* KPI */}
      {view === 'economico' ? (
        <EconomicSection eco={eco} ecoLoading={ecoLoading} ecoTab={ecoTab} setEcoTab={setEcoTab} navigate={navigate}
          reload={reloadEco} deepLink={deepLink} clearDeepLink={clearDeepLink} />
      ) : (
      <>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <KpiCard testid="kpi-to-handle" Icon={Inbox} label="Da gestire" value={stats.to_handle} accent="#FF7819" />
        <KpiCard testid="kpi-resolved" Icon={CheckCircle2} label="Risolte" value={stats.resolved} accent="#059669" />
        <KpiCard testid="kpi-total" Icon={Euro} label="Importo totale da fatturare" value={fmtCurrency(stats.total_to_invoice)} accent="#0078C8" />
      </div>

      {/* Filtri */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex gap-1 bg-[#F5F5F5] rounded-full p-1" data-testid="status-filter">
          {[
            { k: 'open', l: 'Da gestire' },
            { k: 'resolved', l: 'Risolte' },
            { k: 'all', l: 'Tutte' },
          ].map((o) => (
            <button
              key={o.k}
              data-testid={`status-filter-${o.k}`}
              onClick={() => setStatusFilter(o.k)}
              className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                statusFilter === o.k ? 'bg-[#FF7819] text-white' : 'text-[#525252] hover:bg-[#EDEDED]'
              }`}
            >
              {o.l}
            </button>
          ))}
        </div>

        <select
          data-testid="source-filter"
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className="px-3 py-1.5 text-sm rounded-lg border border-[#E4E4E4] bg-white text-[#525252]"
        >
          <option value="">Tutte le origini</option>
          <option value="CONTRACT">Contratto</option>
          <option value="EXTRA_WORK">Extra lavori</option>
          <option value="EXTRA_SUPPLY">Extra forniture</option>
          <option value="FURNITURE">Arredi</option>
        </select>

        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-[#A3A3A3] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca cliente, progetto, riferimento…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-[#E4E4E4] bg-white"
          />
        </div>
      </div>

      {/* Tabella */}
      <div className="rounded-xl border border-[#E4E4E4] bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="billing-queue-table">
            <thead>
              <tr className="bg-[#FAFAFA] text-left text-xs font-semibold text-[#737373] uppercase tracking-wide">
                <th className="px-4 py-3">Data</th>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3">Progetto</th>
                <th className="px-4 py-3">Origine</th>
                <th className="px-4 py-3">Riferimento</th>
                <th className="px-4 py-3">Tranche</th>
                <th className="px-4 py-3 text-right">Importo</th>
                <th className="px-4 py-3">Stato</th>
                <th className="px-4 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-[#A3A3A3]">Caricamento…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-[#A3A3A3]" data-testid="empty-state">Nessuna richiesta trovata</td></tr>
              ) : (
                filtered.map((r) => {
                  const meta = SOURCE_META[r.source_type_canon] || { label: r.source_label || '—', Icon: FileText, color: '#737373' };
                  const Icon = meta.Icon;
                  return (
                    <tr
                      key={r.id || r.request_key}
                      data-testid={`billing-row-${r.id || r.request_key}`}
                      onClick={() => openRow(r)}
                      className="border-t border-[#F0F0F0] hover:bg-[#FFF9F4] cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 text-[#525252] whitespace-nowrap">{fmtDate(r.unlocked_at || r.created_at)}</td>
                      <td className="px-4 py-3 font-medium text-[#171717]">{r.client_name || '—'}</td>
                      <td className="px-4 py-3 text-[#525252]">{r.project_number || '—'}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 font-medium" style={{ color: meta.color }}>
                          <Icon className="w-4 h-4" /> {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[#525252]">{r.source_ref || '—'}</td>
                      <td className="px-4 py-3 text-[#525252] capitalize">{r.tranche_type || '—'}</td>
                      <td className="px-4 py-3 text-right font-semibold text-[#171717] whitespace-nowrap">{fmtCurrency(r.amount)}</td>
                      <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                      <td className="px-4 py-3 text-right">
                        <span className="inline-flex items-center gap-1 text-[#FF7819] font-medium">
                          Apri <ChevronRight className="w-4 h-4" />
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default BillingQueuePage;
